import React, { createRef, useEffect, useState } from "react"
import DayPickerInput from "react-day-picker/DayPickerInput"
import webify from "../util/webify"
import { formatDate, parseDate } from "../util/date"
import { observer } from "mobx-react"
import { format, formatISO, isValid, parse, parseISO, subDays } from "date-fns"

let FORMAT = "M/d/yy"

interface DatePickerWithPresetsProps {
  name?: string
  label: string
  value: string
  isValid: boolean
  onChange?: (date) => void
  relativeToDate?: string
}

const DatePickerOverlayHOC = (label: string, referenceDate: string, changeDate: any) => {
  return observer(({ classNames, selectedDay, children, ...props }: any) => {
    let date = parseISO(referenceDate)

    const presets = [
      { label: label, date: date },
      { label: "30 days prior", date: subDays(date, 30) },
      { label: "45 days prior", date: subDays(date, 45) },
      { label: "60 days prior", date: subDays(date, 60) },
      { label: "90 days prior", date: subDays(date, 90) },
    ]
    return (
      <div className={classNames.overlayWrapper} {...props}>
        <div className={classNames.overlay}>
          {children}
          <div className="p-4" style={{ marginTop: -24 }}>
            <select
              onChange={(e) => {
                changeDate(parseISO(e.target.value))
              }}>
              <option>Presets ...</option>
              {presets.map((p, idx) => (
                <option key={idx} value={formatISO(p.date, { representation: "date" })}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    )
  })
}

let highlightErrorsIf = (condition: boolean): string => (condition ? "border-red-700 " : "")

let DatePickerWithPresets = (props: DatePickerWithPresetsProps) => {
  let initialValue = ""

  let dateValue = props.value && parseISO(props.value)
  if (dateValue && isValid(dateValue)) {
    initialValue = format(dateValue, FORMAT)
  }

  let [date, setDate] = useState(initialValue)
  let hiddenInputRef: any = createRef()

  // this allows us to listen to change events when using this as a
  // web component from svelte
  useEffect(() => {
    let event = new Event("change", { bubbles: true })
    hiddenInputRef.current.dispatchEvent(event)
  })

  return (
    <div className="date-picker-with-presets-component" data-role={props["data-role"]}>
      <input
        type="hidden"
        name={props.name}
        value={
          isValid(parse(date, FORMAT, new Date())) && formatISO(parse(date, FORMAT, new Date()), { representation: "date" })
        }
        ref={hiddenInputRef}
      />

      <DayPickerInput
        format={FORMAT}
        formatDate={formatDate}
        parseDate={parseDate}
        placeholder={format(new Date(), FORMAT)}
        onDayChange={(day, _, input) => {
          if (day) {
            setDate(input.getInput().value)
          }

          props.onChange?.(day)
        }}
        value={date}
        dayPickerProps={{
          fromMonth: new Date(2019, 1, 1),
          toMonth: new Date(2029, 1, 1),
        }}
        overlayComponent={DatePickerOverlayHOC(props.label, props.relativeToDate, (date) => {
          setDate(format(date, FORMAT))

          props.onChange?.(date)
        })}
        inputProps={{
          className: `${highlightErrorsIf(!props.isValid)} form-control date-picker`,
          "data-role": "internal-input",
          pattern: "\\d\\d?/\\d\\d?/\\d\\d",
          title: `Please format dates like this: ${format(new Date(), FORMAT)}`,
          type: "text",
        }}
      />
    </div>
  )
}

export default DatePickerWithPresets
webify("date-picker-with-presets", DatePickerWithPresets)
